import { titleCase } from '../titleCase.ts';
import type { ProjectProcess, ProcessStep } from './graph.ts';
import type { PhaseKey } from './phases.ts';
import type { Schedule } from './schedule.ts';
import { chooseOf, type DecisionChoose, type TreeOption } from './schema.ts';
import { isReached, openTasks } from './standing.ts';

/**
 * What the client sees of the process (process-flow spec, "Client
 * presentation"): quiet by default, what waits on them in the foreground, each
 * step by its one name (design study 31, slice 3).
 */

export interface StepInfo {
  /** NSB's own note: never the client's to read. */
  why: string | null;
  /** What the client reads when they open the step (Jason, 2026-10-06): the tree's note for them, none when they see its tasks only. */
  clientNote?: string | null;
  owner: string | null;
  /** A design review's step: the client finishes it on the review, never with a Done here. */
  review?: boolean;
  /** A step carrying the job's pattern language (spec "Pattern Mapping" §4.5): finished by its lock, on Patterns, never with a Done here. */
  patterns?: boolean;
  /** It delivers an estimate and none is set yet (design study 17): NSB's turn before the client's, so a gate of theirs waits on NSB. */
  estimateMissing?: boolean;
  /**
   * Something besides its own lines holds it: a gate or inspection not done anywhere before it, however far back (L-17
   * B, core `gateHolds`: the waits, a step's list around an inspection, and the gate that opens its phase), a gate whose
   * phase has steps not done (a gate holds its whole phase, core `phaseHolds`), or a step that came late, or sits in
   * one, not yet confirmed (design study 21). Not reached until it is not.
   */
  held?: boolean;
  /**
   * A decision's options, its tree node's (design study 22): the words NSB wrote on the master for the client to choose
   * from, so the client reads them. None on a step of the job's own, or a decision without.
   */
  options?: TreeOption[];
  /** Whether one of its options is chosen, or any number of them. */
  choose?: DecisionChoose;
}

/**
 * The options chosen deciding a decision (design study 22; Jason, 2026-10-07: "either select one of many or many of
 * many"): checked against what its tree node offers, in the order it offers them, each once. A decision without options
 * is decided as before (`chosen` empty). Refused in words: none chosen, several where one is chosen, an option it does
 * not offer.
 */
export function decisionChoice(
  d: { title: string; options?: readonly TreeOption[] | undefined; choose?: DecisionChoose | undefined },
  ids: readonly string[] | undefined,
): { chosen: TreeOption[] } | { refused: string; code: 'choice' | 'bad_choice' } {
  const name = titleCase(d.title);
  const options = d.options ?? [];
  const picked = [...new Set(ids ?? [])];
  if (!options.length) return picked.length ? { refused: `${name} has no options to choose from.`, code: 'bad_choice' } : { chosen: [] };
  const one = chooseOf(d) === 'one';
  if (!picked.length) return { refused: `${name} is decided by choosing ${one ? 'one of its options' : 'one or more of its options'}.`, code: 'choice' };
  const unknown = picked.find((id) => !options.some((o) => o.id === id));
  if (unknown !== undefined) return { refused: `${name} has no such option.`, code: 'bad_choice' };
  if (one && picked.length > 1) return { refused: `${name} is decided by choosing one of its options, not several.`, code: 'bad_choice' };
  return { chosen: options.filter((o) => picked.includes(o.id)).map((o) => ({ id: o.id, text: o.text })) };
}

/**
 * The work has reached a step and nothing else holds it: its lines (core `isReached`), every gate and inspection before
 * it done (L-17 B), its estimate where it delivers one, its phase where it is the gate that closes it, its confirmation
 * where it came late, and for a gate its own tasks (L-23: the Quote waits on its render, NSB's turn before the client
 * approves it, as its estimate is). The one rule for what waits on the client and what they can act on (`clientSteps`,
 * the decide route, their drawing): what holds a step back from NSB holds it back from them. `phases`, the job's (core
 * `projectPhases`), as `isReached` reads them: a task done early waits on nothing its step waits on.
 */
export function reachedFor(p: ProjectProcess, id: string, info: (id: string) => StepInfo | undefined, phases?: ReadonlyMap<string, PhaseKey>): boolean {
  const i = info(id);
  const s = p.steps.find((x) => x.id === id);
  return workReached(p, id, info, phases) && !i?.estimateMissing && (s?.kind !== 'gate' || openTasks(p, id).length === 0);
}

/**
 * The work has reached a step: its lines (core `isReached`) and nothing holds it (a gate or inspection before it, its
 * phase's gate, a late step not confirmed). Not started, it is ready to start (Working on, design study 31), its estimate
 * or a gate's tasks still to come: those are for finishing it, not for starting it (`reachedFor` adds them). `phases` as
 * `isReached` reads them.
 */
export function workReached(p: ProjectProcess, id: string, info: (id: string) => StepInfo | undefined, phases?: ReadonlyMap<string, PhaseKey>): boolean {
  return isReached(p, id, phases) && !info(id)?.held;
}

/** Who does a step, as the client reads it: you, NSB, or others (a sub, an engineer, the inspector). */
export type ClientOwner = 'you' | 'nsb' | 'others';
export const clientOwner = (owner: string | null): ClientOwner => (owner === 'client' ? 'you' : owner === 'nsb' || owner === null ? 'nsb' : 'others');

/** One of the client's own steps, as their Decisions page lists it. */
export interface ClientOwnStep {
  id: string;
  title: string;
  /** A gate here is one the client approves (design study 09, gates A). */
  kind: 'decision' | 'work' | 'gate';
  why: string | null;
  /** On the chain that sets the next date or the end: most pressing. */
  critical: boolean;
  doneOn: string | null;
  /** A decision's options to choose from, and whether one is chosen or any (design study 22); none for a decision without. */
  options?: TreeOption[];
  choose?: DecisionChoose;
}

/** What waits on the client now: their own steps the work has reached (`clientSteps`), most pressing first. */
export interface NowDto {
  yours: Omit<ClientOwnStep, 'doneOn'>[];
}

/** What waits on the client now, as their home and their emails read it; `closed` and `phases` as `clientSteps` reads them. */
export function clientNow(p: ProjectProcess, sch: Schedule, info: (id: string) => StepInfo | undefined, closed = false, phases?: ReadonlyMap<string, PhaseKey>): NowDto {
  return { yours: clientSteps(p, sch, info, closed, phases).waiting.map(({ doneOn: _d, ...y }) => y) };
}

/**
 * The client's own steps (Jason, 2026-10-06: a step the client does or decides "would show up on the waiting on you
 * tab and the decisions list in the client view when it is reached by the work flow"; Still to decide, A): waiting on
 * them now, the work having reached it, the most pressing first; coming up, not reached yet, in the order the work
 * comes to them; and made, the latest first. Under the names the client sees the process by; a design review is
 * theirs on the review, and a step carrying the pattern language by its lock, never here; a gate is theirs when they
 * approve it (design study 09, gates A). On a job closed to its client (`closed`: cancelled, or archived, kept as it
 * ended, L-19 A; core `clientClosed`) nothing waits on them or comes up, and what they made stays: the one rule their
 * Waiting on you, their emails, their Decisions page and their drawing read. `phases`, the job's (core `projectPhases`):
 * a task of theirs done early is reached by its own waits (`reachedFor`).
 */
export function clientSteps(
  p: ProjectProcess,
  sch: Schedule,
  info: (id: string) => StepInfo | undefined,
  closed = false,
  phases?: ReadonlyMap<string, PhaseKey>,
): { waiting: ClientOwnStep[]; coming: ClientOwnStep[]; made: ClientOwnStep[] } {
  const live = p.steps.filter((s) => !s.off);
  const leaf = (s: ProcessStep) => !live.some((c) => c.parentId === s.id);
  // Their decisions and work, and the gates they approve (gates A): a step holding a list is done by its tasks, the
  // client's listed on their own (L-23), but a gate is approved whole, once its tasks are done (`reachedFor`).
  const theirs = live.filter((s) => clientOwner(info(s.id)?.owner ?? null) === 'you' && (leaf(s) || s.kind === 'gate') && !info(s.id)?.review && !info(s.id)?.patterns);
  const own = (s: ProcessStep): ClientOwnStep => {
    const i = info(s.id);
    const options = s.kind === 'decision' ? (i?.options ?? []) : [];
    return {
      id: s.id,
      title: s.title,
      kind: s.kind === 'decision' ? 'decision' : s.kind === 'gate' ? 'gate' : 'work',
      why: i?.clientNote ?? null,
      critical: Boolean(sch.timing.get(s.id)?.critical || sch.timing.get(s.id)?.drivesNext),
      doneOn: s.doneOn ?? null,
      // What they choose from deciding it (design study 22).
      ...(options.length ? { options: options.map((o) => ({ id: o.id, text: o.text })), choose: chooseOf(i ?? {}) } : {}),
    };
  };
  const rank = (s: ProcessStep) => sch.rank.get(s.id) ?? Number.MAX_SAFE_INTEGER;
  // Closed to them, cancelled or archived (L-19 A): nothing open is theirs to act on any more.
  const open = closed ? [] : theirs.filter((s) => s.state !== 'done');
  // Reached by the work, with its estimate set where it delivers one (the Quote the client approves is NSB's to price
  // first), and nothing else holding it: its phase, for the gate that closes it, or its confirmation, come late.
  const reached = (s: ProcessStep) => reachedFor(p, s.id, info, phases);
  return {
    waiting: open.filter(reached).map(own).sort((a, b) => Number(b.critical) - Number(a.critical)),
    coming: open.filter((s) => !reached(s)).sort((a, b) => rank(a) - rank(b)).map(own),
    made: theirs.filter((s) => s.state === 'done').sort((a, b) => (b.doneOn ?? '').localeCompare(a.doneOn ?? '')).map(own),
  };
}
